import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { STATUS_MAP, isOverdue, parseDate, addDays, daysBetween, toISODate } from "@/lib/taskConstants";
import { EVENT_TYPE_MAP } from "@/components/calendar/EventDialog";

const DAY_W = 26;
const LABEL_W = 220;
const MONTHS_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const clampOff = (v, max) => Math.min(Math.max(v, 0), max);

const buildMonthSpans = (rangeStart, totalDays) => {
  const spans = [];
  let cursor = parseDate(rangeStart);
  let remaining = totalDays;
  while (remaining > 0) {
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const span = Math.min(daysInMonth - cursor.getDate() + 1, remaining);
    spans.push({ label: `${MONTHS_SHORT[cursor.getMonth()]} ${cursor.getFullYear()}`, days: span });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    remaining -= span;
  }
  return spans;
};

const GanttBar = ({ start, end, rangeStart, totalDays, color, label, faded, testId, onClick }) => {
  const startOff = clampOff(daysBetween(rangeStart, start), totalDays - 1);
  const endOff = clampOff(daysBetween(rangeStart, end), totalDays - 1);
  return (
    <button onClick={onClick} data-testid={testId}
      className="absolute top-1.5 h-5 rounded-md flex items-center px-1.5 hover:opacity-90 shadow-sm overflow-hidden"
      style={{
        left: startOff * DAY_W + 1,
        width: Math.max((endOff - startOff + 1) * DAY_W - 3, DAY_W - 3),
        backgroundColor: color,
        opacity: faded ? 0.85 : undefined,
      }}>
      <span className="text-[10px] text-white font-medium truncate pointer-events-none">{label}</span>
    </button>
  );
};

const GanttRow = ({ indent, labelIcon, label, done, testId, onLabelClick, children, gridBg }) => (
  <div className="flex border-b border-border/50 hover:bg-secondary/30" data-testid={testId}>
    <button onClick={onLabelClick} disabled={!onLabelClick}
      className={`shrink-0 border-r py-2 text-xs text-left truncate flex items-center gap-1 ${onLabelClick ? "hover:text-primary" : ""}`}
      style={{ width: LABEL_W, paddingLeft: 12 + indent * 14, paddingRight: 8 }}>
      {labelIcon && <span className="shrink-0 text-[10px]">{labelIcon}</span>}
      <span className={`truncate ${done ? "line-through text-muted-foreground" : ""}`}>{label}</span>
    </button>
    <div className="relative h-8" style={gridBg}>{children}</div>
  </div>
);

export const CalendarGantt = ({ events, tasks, sources, colorMap, rangeStart, rangeEnd, today, onEvent, onTask }) => {
  const [collapsed, setCollapsed] = useState({});
  const totalDays = daysBetween(rangeStart, rangeEnd) + 1;
  const monthSpans = useMemo(() => buildMonthSpans(rangeStart, totalDays), [rangeStart, totalDays]);
  const dayCells = useMemo(
    () => Array.from({ length: totalDays }, (_, i) => parseDate(addDays(rangeStart, i))),
    [rangeStart, totalDays]
  );
  const todayOffset = daysBetween(rangeStart, today);

  const groups = useMemo(() => {
    const out = [];
    for (const s of sources) {
      const evs = events
        .filter((e) => e.source === s.id)
        .sort((a, b) => a.start_date.localeCompare(b.start_date) || a.title.localeCompare(b.title));
      const ts = tasks.filter((t) => t.source === s.id);
      if (!evs.length && !ts.length) continue;
      const projMap = {};
      for (const t of ts) {
        (projMap[t.project_id] = projMap[t.project_id] ||
          { id: t.project_id, name: t.project_name, icon: t.project_icon, tasks: [] }).tasks.push(t);
      }
      const projects = Object.values(projMap).sort((a, b) => a.name.localeCompare(b.name));
      for (const p of projects) {
        p.tasks.sort((a, b) => (a.start_date || a.due_date).localeCompare(b.start_date || b.due_date));
      }
      out.push({ source: s, events: evs, projects });
    }
    return out;
  }, [sources, events, tasks]);

  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const gridBg = {
    width: totalDays * DAY_W,
    backgroundImage: `repeating-linear-gradient(to right, hsl(var(--border) / 0.55) 0px, hsl(var(--border) / 0.55) 1px, transparent 1px, transparent ${DAY_W}px)`,
  };

  if (groups.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-10 text-center" data-testid="calendar-gantt-empty">
        <p className="text-2xl mb-2">📊</p>
        <p className="text-sm text-muted-foreground">No hay eventos ni tareas con fechas en este periodo.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="calendar-gantt">
      <div className="overflow-x-auto">
        <div style={{ minWidth: LABEL_W + totalDays * DAY_W }}>
          {/* Cabecera meses */}
          <div className="flex border-b bg-secondary/40">
            <div className="shrink-0 border-r px-3 py-1.5 text-xs font-semibold text-muted-foreground" style={{ width: LABEL_W }}>
              Espacio / Proyecto
            </div>
            <div className="flex">
              {monthSpans.map((m) => (
                <div key={m.label} style={{ width: m.days * DAY_W }}
                  className="text-xs font-semibold text-muted-foreground px-2 py-1.5 border-r truncate">
                  {m.label}
                </div>
              ))}
            </div>
          </div>
          {/* Cabecera días */}
          <div className="flex border-b">
            <div className="shrink-0 border-r" style={{ width: LABEL_W }} />
            <div className="flex">
              {dayCells.map((d) => {
                const iso = toISODate(d);
                const weekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <div key={iso} style={{ width: DAY_W }}
                    className={`text-center text-[10px] py-1 border-r border-border/40 ${
                      iso === today ? "bg-primary/10 text-primary font-bold"
                        : weekend ? "bg-secondary/40 text-muted-foreground" : "text-muted-foreground"
                    }`}>
                    {d.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Filas */}
          <div className="relative">
            {todayOffset >= 0 && todayOffset < totalDays && (
              <div className="absolute top-0 bottom-0 w-px bg-destructive z-10 pointer-events-none"
                style={{ left: LABEL_W + todayOffset * DAY_W + DAY_W / 2 }} data-testid="cgantt-today-line" />
            )}
            {groups.map(({ source: s, events: evs, projects }) => (
              <div key={s.id}>
                {/* Cabecera Espacio (plegable) */}
                <div className="flex border-b bg-secondary/50" data-testid={`cgantt-space-${s.id}`}>
                  <button onClick={() => toggle(s.id)} data-testid={`cgantt-collapse-${s.id}`}
                    className="shrink-0 border-r px-2 py-2 text-xs font-semibold text-left flex items-center gap-1.5 hover:text-primary"
                    style={{ width: LABEL_W }}>
                    {collapsed[s.id] ? <ChevronRight className="h-3.5 w-3.5 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0" />}
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                    <span className="truncate">{s.icon} {s.name}</span>
                    {collapsed[s.id] && (
                      <span className="text-[10px] text-muted-foreground font-normal shrink-0">
                        ({evs.length + projects.reduce((n, p) => n + p.tasks.length, 0)})
                      </span>
                    )}
                  </button>
                  <div style={{ width: totalDays * DAY_W }} />
                </div>
                {!collapsed[s.id] && (
                  <>
                    {evs.map((ev) => (
                      <GanttRow key={`${ev.id}-${ev.start_date}`} indent={1} done={false}
                        labelIcon={EVENT_TYPE_MAP[ev.event_type]?.icon} label={ev.title}
                        testId={`cgantt-event-row-${ev.id}`} onLabelClick={() => onEvent(ev)} gridBg={gridBg}>
                        <GanttBar start={ev.start_date} end={ev.end_date} rangeStart={rangeStart} totalDays={totalDays}
                          color={ev.color || colorMap[ev.source]} label={ev.title}
                          testId={`cgantt-event-${ev.id}`} onClick={() => onEvent(ev)} />
                      </GanttRow>
                    ))}
                    {projects.map((p) => (
                      <div key={p.id}>
                        <div className="flex border-b border-border/50 bg-primary/[0.04]">
                          <div className="shrink-0 border-r py-1.5 pl-6 pr-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider truncate"
                            style={{ width: LABEL_W }} data-testid={`cgantt-project-${p.id}`}>
                            {p.icon} {p.name}
                          </div>
                          <div style={{ width: totalDays * DAY_W }} />
                        </div>
                        {p.tasks.map((t) => {
                          const start = t.start_date || t.due_date;
                          const end = t.due_date || t.start_date;
                          const color = isOverdue(t) ? "#D93025" : STATUS_MAP[t.status]?.color;
                          return (
                            <GanttRow key={t.id} indent={t.parent_task_id ? 3 : 2} done={t.status === "done"}
                              labelIcon={t.parent_task_id ? "└" : null} label={t.title}
                              testId={`cgantt-task-row-${t.id}`} onLabelClick={() => onTask(t)} gridBg={gridBg}>
                              <GanttBar start={start} end={end} rangeStart={rangeStart} totalDays={totalDays}
                                color={color} label={t.title} faded={!!t.parent_task_id}
                                testId={`cgantt-task-${t.id}`} onClick={() => onTask(t)} />
                            </GanttRow>
                          );
                        })}
                      </div>
                    ))}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 px-3 py-2 border-t bg-secondary/30 flex-wrap">
        {[["#9AA0A6", "Pendiente"], ["#1A73E8", "En progreso"], ["#9D4CDD", "En revisión"], ["#1E8E3E", "Completada"], ["#D93025", "Vencida"]].map(([c, l]) => (
          <span key={l} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: c }} /> {l}
          </span>
        ))}
        <span className="text-[11px] text-muted-foreground ml-auto">Los eventos usan el color de su calendario</span>
      </div>
    </div>
  );
};
